import { isBlob } from "es-toolkit";
import mime from "mime";
import { last } from "lodash";
import { saveAs } from "file-saver";

export enum FileType {
  IMAGE = "image",
  PDF = "pdf"
}

export function readFileToDataUrl(file: Blob): Promise<string> {
  return new Promise((res, rej) => {
    const fileReader = new FileReader();

    fileReader.onabort = fileReader.onerror = (error) => {
      rej(error);
    };

    fileReader.onload = (event) => {
      if (event.target) {
        res(<string>event.target.result);
      } else {
        rej("event.target undefined!");
      }
    };

    fileReader.readAsDataURL(file);
  });
}

export function getFileSizeFromBase64(base64: string): number {
  const padLenght = base64.endsWith("==") ? 2 : 1;
  return base64.length * (3 / 4) - padLenght;
}

export function blobToFile(blob: Blob, name: string): File {
  return new File([blob], name);
}

export function getMimeType(fileName: string): string | null {
  return mime.getType(fileName);
}

export function getExtension(fileName: string): string {
  return (last(fileName.split(".") || ["unknow"]) || "").toLowerCase();
}

export function fileSaveAs({
  fileName,
  contentType,
  content
}: {
  fileName: string;
  content: string | Blob;
  contentType: string;
}): void {
  switch (true) {
    case isBlob(content): {
      saveAs(content, fileName);
      break;
    }
    default: {
      saveAs(new Blob([content], { type: contentType }), fileName);
      break;
    }
  }
}

export function pickFile(config?: {
  acceptedFileTypes?: string[];
}): Promise<File | null> {
  return new Promise<File | null>((res, rej) => {
    const inputFile = document.createElement("input");
    inputFile.setAttribute("type", "file");

    if (config?.acceptedFileTypes) {
      inputFile.setAttribute("accept", config.acceptedFileTypes.join(", "));
    }

    inputFile.classList.add("hidden");

    inputFile.oncancel = () => {
      document.body.removeChild(inputFile);
      res(null);
    };

    inputFile.onchange = (inputFileChangeEvent) => {
      document.body.removeChild(inputFile);
      res(inputFile.files?.item(0) || null);
    };

    document.body.append(inputFile);

    inputFile.click();
  });
}

export function pickFiles(config?: {
  acceptedFileTypes?: string[];
}): Promise<File[] | null> {
  return new Promise<File[] | null>((res, rej) => {
    const inputFile = document.createElement("input");
    inputFile.setAttribute("type", "file");
    inputFile.setAttribute("multiple", "true");

    if (config?.acceptedFileTypes) {
      inputFile.setAttribute("accept", config.acceptedFileTypes.join(", "));
    }

    inputFile.classList.add("hidden");

    inputFile.oncancel = () => {
      document.body.removeChild(inputFile);
      res(null);
    };

    inputFile.onchange = (inputFileChangeEvent) => {
      document.body.removeChild(inputFile);

      // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
      res(Array.from(inputFile.files).map((file) => file) || null);
    };

    document.body.append(inputFile);

    inputFile.click();
  });
}

export function getFileTypeByFileName(fileName: string): FileType | string {
  let fileExt = getExtension(fileName);

  const fileTypeMapList = [
    {
      type: FileType.IMAGE,
      extList: ["gif", "tiff", "jpg", "png", "jpeg", "tif", "jpg", "bmp"]
    },
    {
      type: FileType.PDF,
      extList: ["pdf"]
    }
  ];

  return (
    fileTypeMapList.find((map) => map.extList.includes(fileExt))?.type ??
    fileExt
  );
}

export function isImage(fileName: string): boolean {
  return getFileTypeByFileName(fileName) === FileType.IMAGE;
}

export function isPdf(fileName: string): boolean {
  return getFileTypeByFileName(fileName) === FileType.PDF;
}
